import { useMemo } from 'react';

import { Listbox } from '@/components/ui/listbox';
import type { PopoverPortalTarget } from '@/components/ui/popover-portal';
import { sortedCountries, type CountryCode } from '@/constants/countries';
import { usePreferences } from '@/stores/preferences-store';

/**
 * Country selection with flag and dial code, named in the console's language.
 *
 * Names come from `Intl.DisplayNames`, so this list is translated without Trinity
 * maintaining a translation of every country in every locale.
 */

export interface CountrySelectProps {
  value: CountryCode | null;
  onChange: (country: CountryCode | null) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  /** Codes to float to the top — usually the church's own country. */
  priority?: CountryCode[];
  showDialCode?: boolean;
  disabled?: boolean;
  invalid?: boolean;
  clearable?: boolean;
  clearLabel?: string;
  id?: string;
  popoverPortal?: PopoverPortalTarget;
  className?: string;
  'aria-describedby'?: string;
}

export const CountrySelect = ({
  value,
  onChange,
  placeholder,
  searchPlaceholder,
  emptyMessage,
  priority,
  showDialCode = false,
  disabled,
  invalid,
  clearable,
  clearLabel,
  id,
  popoverPortal = 'body',
  className,
  ...aria
}: CountrySelectProps) => {
  const locale = usePreferences((state) => state.locale);

  const options = useMemo(
    () =>
      sortedCountries(locale, priority).map((country) => ({
        value: country.code,
        label: `${country.flag}  ${country.name}`,
        description: showDialCode ? country.dial : undefined,
        keywords: [country.code, country.dial],
      })),
    [locale, priority, showDialCode],
  );

  return (
    <Listbox<CountryCode>
      value={value}
      onChange={onChange}
      options={options}
      searchable
      placeholder={placeholder}
      searchPlaceholder={searchPlaceholder}
      emptyMessage={emptyMessage}
      disabled={disabled}
      invalid={invalid}
      clearable={clearable}
      clearLabel={clearLabel}
      id={id}
      popoverPortal={popoverPortal}
      className={className}
      {...aria}
    />
  );
};
